import { FC, Fragment } from "react";
import { Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { generateDateLabelByBudgetPeriod } from "./utils/helpers/generateDateLabelByBudgetPeriod";
import { money } from "@/utils/numeral/money";
import { getNumberByPercentage } from "@/utils/numeral/getPercentage";
import { BudgetPeriod } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { isNotStartedYet } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/isNotStartedYet";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";

interface ResourceExplorerBudgetTooltipProps {
  name?: string;
  progress: number;
  amount: number;
  used?: number;
  startDate?: string;
  endDate?: string;
  period: BudgetPeriod;
  hideDateLabel?: boolean;
}

export const ResourceExplorerBudgetTooltipContent: FC<
  ResourceExplorerBudgetTooltipProps
> = ({
  startDate,
  endDate,
  period,
  progress,
  used,
  name,
  amount,
  hideDateLabel,
}) => {
  const isNotStarted = isNotStartedYet(startDate);
  const overspend: boolean = progress > 100;
  const dateLabel = startDate
    ? generateDateLabelByBudgetPeriod({ startDate, endDate, period })
    : period;

  const balanceCost = getNumberByPercentage(100 - progress, amount);
  return (
    <Stack
      sx={{
        p: 1,
        whiteSpace: "nowrap",
      }}
    >
      {name && (
        <TypographyWithTooltip
          title={name}
          placement="top"
          pb={1}
          variant="caption"
          color="text.secondary"
          fontWeight="medium"
        />
      )}
      <Typography variant="caption" textTransform="capitalize">
        {!hideDateLabel && dateLabel} Budget:{" "}
        <Typography variant="caption" fontWeight="bold" component="span">
          {money(amount)}
        </Typography>
      </Typography>

      {isNotStarted ? (
        <Typography variant="caption" mt={0.5}>
          Start Date:{" "}
          <Typography variant="caption" fontWeight="bold" component="span">
            {formatDate(startDate ?? "", {
              type: "date",
            })}
          </Typography>
        </Typography>
      ) : (
        <Fragment>
          <Typography variant="caption" mt={0.5}>
            Used:{" "}
            <Typography variant="caption" fontWeight="bold" component="span">
              {money(used)} ({progress}%)
            </Typography>
          </Typography>

          <Typography variant="caption" mt={0.5}>
            {overspend ? "Overspend: " : "Left: "}
            <Typography
              variant="caption"
              fontWeight="bold"
              component="span"
              color={overspend ? "error.main" : "inherit"}
            >
              {overspend ? money(Math.abs(balanceCost)) : money(balanceCost)}{" "}
              {progress < 100
                ? `(${parseFloat((100 - progress).toFixed(2))}%)`
                : ""}
            </Typography>
          </Typography>
        </Fragment>
      )}
    </Stack>
  );
};
